import Recurrence from '../classes/Recurrence';
import DateRestriction from '../classes/DateRestriction';
import moment from 'moment-timezone';
import TicketType from './TicketType';
import VatRepository from '../services/VatRepository';

export default class Event {
    STATUS_PRIVATE: string = 'private';

    public apply_sell_until_to_tickets: boolean = false;
    public currency:string = 'gbp';
    public date:string = '';
    public linked_occurrence_next_date:string|null = null;
    public display_vat:boolean = false;
    public end_date:string|null = null;
    public end_time:string|null = null;
    public hide_sell_until:boolean = false;
    public hide_sold_out_tickets:boolean = false;
    public hide_urgency_message:boolean = false;
    public is_sold_out:boolean = false;
    public photo:string = '';
    public recurrence:{
        frequency: string,
        string: string,
        weekdays: any[],
        end_date: string,
        custom_dates: any
    } | null = null;
    public recurrenceModel: Recurrence;
    public require_time_slot_selection: boolean = false;
    public sell_until:number|null = null;
    public show_waiting_list: boolean = true;
    public skip_sold_out_dates: boolean = false;
    public start_time:string = '';
    public status:string = '';
    public ticket_sort_by:string = '';
    public ticket_type_vat_rate:string|null = null;
    public ticket_types:any[] = [];
    public tickets_available_at_door:boolean = false;
    public timezone:string = 'Europe/London';
    public title:string = '';
    public urgency_labels:any = [];
    public venue_title:string = '';
    public venue_address:any = '';
    public _id:string = '';

    public google_tag_manager_id:string = '';

    // event list description reveal on hover
    public event_list_description: string = '';
    public showEventListDescription: boolean = false;

    // cache store of ticket type lists
    public sellableTicketTypes: any = {};

    // ticket type index for quick finding of ticket types
    public ticketTypeIndex: any = {};

    constructor (eventObject: any = {}) {
        // get each key

        if(eventObject.linked_occurrence_next_date){
            this.linked_occurrence_next_date = eventObject.linked_occurrence_next_date;
        }
        for(let key in eventObject) {
            if (this.hasOwnProperty(key)) {
                (<any>this)[key] = eventObject[key];
            }
        }

        this.recurrenceModel = new Recurrence(this);

        this.start_time = this._convertTimezone(this.start_time);
        this.end_time = this._convertTimezone(this.end_time);

        if(this.ticket_types) {
            this.prepareTicketTypes();

            // sort ticket types
            this.sortTicketTypes();
        }
        else {
            this.ticket_types = [];
        }
    }

    /**
     * @desc sets up the TicketTypes model with default data and filters out the ones that are not sellable
     */
    prepareTicketTypes() {
        this.ticket_types = this.ticket_types.map((ticketType, index) => {
            ticketType = new TicketType(ticketType);

            return ticketType;
        });

        // filter out tickets that are not on sale yet
        this.ticket_types = this.ticket_types.filter((ticketType) => {
            return this.ticketTypeIsSellable(ticketType);
        });

        // map event start time on to ticket missing their start time
        this.ticket_types = this.ticket_types.map((ticketType, index) => {
            ticketType.start_time = ticketType.start_time ? this._convertTimezone(ticketType.start_time) : this.start_time;
            ticketType.end_time = ticketType.end_time ? this._convertTimezone(ticketType.end_time) : this.end_time;

            return ticketType;
        });

        this.ticket_types = this.ticket_types.map((ticketType, index) => {
            ticketType.date_restrictions = new DateRestriction(ticketType.date_restrictions);
            this.ticketTypeIndex[ticketType.id] = index;

            return ticketType;
        });
    }

    /**
     * @desc Returns true if ticket type is public and not deleted
     * @param {TicketType}
     * @returns {boolean}
     */
    ticketTypeIsSellable(ticketType: TicketType) : boolean {
        return !ticketType.deleted && ticketType.status !== this.STATUS_PRIVATE;
    }

    /**
     * @desc Returns the specified ticketType
     * @param {string} ticketTypeId
     * @returns {TicketType}
     */
    getTicketType(ticketTypeId: string) : TicketType|any {
        for(let i = 0; i < this.ticket_types.length; i++) {
            if(this.ticket_types[i].id === ticketTypeId) {
                return this.ticket_types[i];
            }
        }
    }

    /**
     * @desc Returns all the ticketTypes
     * @returns {Array<TicketType>}
     */
    getTicketTypes() : Array<TicketType> {
        return this.ticket_types;
    }

    /**
     * @desc Returns sellable tickets that satisfy dateRestrictions on a specified date
     * @param {moment.Moment}
     * @returns {Array<TicketType>}
     */
    getSellableTicketTypesOnDate(date: moment.Moment) : Array<TicketType> {
        let sellableTicketTypesDateKey = date.format( 'YYYYMMDD' );
        if(!this.sellableTicketTypes[sellableTicketTypesDateKey]){
            this.sellableTicketTypes[sellableTicketTypesDateKey] = this.ticket_types.filter((ticketType) => {
                return !ticketType.date_restrictions.isDateRestricted(date);
            });
        }

        return this.sellableTicketTypes[sellableTicketTypesDateKey];
    }

    /**
     * @desc Returns has sellable tickets that satisfy dateRestrictions on a specified date
     * @param {moment.Moment}
     * @returns {boolean}
     */
    hasSellableTicketTypesOnDate(date:moment.Moment ) : boolean {
        let sellableTicketTypes = this.getSellableTicketTypesOnDate(date);

        return !!(sellableTicketTypes && sellableTicketTypes.length > 0);
    }

    /**
     * @desc Returns array of prices for the sellable tickets
     * @param {moment.Moment} onDate
     * @return {Array<string>} of price strings
     */
    getPriceRange(onDate:moment.Moment) : Array<string> {
        let sellableTicketTypes = onDate ? this.getSellableTicketTypesOnDate( onDate ) : this.getTicketTypes();

        let ticketPrices = sellableTicketTypes.reduce((prices: any, ticketType:any) => {
            if(prices.indexOf( ticketType.price ) === -1){
                prices.push(parseFloat(ticketType.price));
            }

            return prices;
        }, []);

        ticketPrices.sort((a:any, b:any) => {
            if(a === b){
                return 0;
            }

            return a > b ? 1 : -1;
        });

        return ticketPrices;
    }

    /**
     * @desc Returns the lowest price on date (returns 'Free' if lowest is 0)
     * @param  {moment.Moment} onDate
     * @returns {string}
     */
    getLowestPrice(onDate:moment.Moment) : string|null {
        let ticketPrices = this.getPriceRange( onDate );

        return ticketPrices.length > 0 ? ticketPrices[0] : null;
    }

    /**
     * @desc Gets ticket type start time
     * @param  {string} ticketTypeId
     * @returns {string|boolean}
     */
    getTicketTypeStartTime(ticketTypeId: string) : any {
        let ticketType = this.getTicketType(ticketTypeId);

        return ticketType.start_time || this.start_time;
    }

    /**
     * @desc gets ticket type end time
     * @param  {string} ticketTypeId
     * @returns {string|boolean}
     */
    getTicketTypeEndTime(ticketTypeId: string) : any {
        let ticketType = this.getTicketType(ticketTypeId);

        return ticketType.end_time || this.end_time;
    }

    /**
     * @desc gets ticket type price
     * @param  {string} ticketTypeId
     * @returns {string}
     */
    getTicketTypePriceExcludingVAT(ticketTypeId: string) : any {
        const ticketType = this.getTicketType(ticketTypeId);

        const vatRate = VatRepository.getByTicketType(this, ticketType) + 1;

        const priceExcludingVAT = ticketType.price / vatRate;

        return priceExcludingVAT.toFixed(2);
    }

    /**
     * @desc should the widget display urgency labels
     * @returns {boolean}
     */
    showUrgencyLabels() : boolean {
        return !this.hide_urgency_message;
    }

    /**
     * @desc should the widget display sold out tickets
     * @returns {boolean}
     */
    showSoldOutTicketTypes() : boolean {
        return !this.hide_sold_out_tickets;
    }

    /**
     * @desc sorts based on event.tickets_sort_by
     */
    sortTicketTypes() {
        switch(this.ticket_sort_by) {
            case 'ticket_name':
                this.ticket_types.sort((ticketA:TicketType, ticketB:TicketType) => ticketA.ticket_type < ticketB.ticket_type ? -1 : ticketA.ticket_type > ticketB.ticket_type ? 1 : 0);
                break;

            case 'start_time':
                this.ticket_types.sort((ticketA:TicketType, ticketB:TicketType) => ticketA.start_time < ticketB.start_time ? -1 : ticketA.start_time > ticketB.start_time ? 1 : 0);
                break;
        }
    }

    /**
     * @desc gets the date description
     * @returns {string}
     */
    getDateDescription() : string {
        let dateDescription: any = '';

        if(this.end_date) {
            let startDate = moment(this.date);
            let endDate = moment(this.end_date);

            dateDescription = `${startDate.format( 'Do MMM YYYY' )} - ${endDate.format( 'Do MMM YYYY' )}`;
        }
        else {
            dateDescription = this.recurrenceModel.getRecurrenceDescription();
        }

        return dateDescription;
    }

    /**
     * @desc Converts timezone
     * @param {string} startTime
     * @return {string}
     */
    _convertTimezone(startTime:any) : string {
        let convertedTime = '';

        if(startTime) {
            const timeParts = startTime.split(':');

            if(timeParts.length === 2) {
                let timeToConvert = moment.tz('Europe/London');

                timeToConvert.hour(timeParts[0]).minute(timeParts[1]);
                timeToConvert.tz(this.timezone);

                convertedTime = timeToConvert.format('HH:mm');
            }
        }

        return convertedTime;
    }
}
